<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type { Wallet } from '@/net/api';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import { fuelCountdown } from './format';
import FuelIcon from '@/ui/FuelIcon.vue';

/**
 * The fuel gauge, with a live countdown to the next unit.
 *
 * The server never pushes fuel — it stores a reading and a timestamp and
 * derives the amount on read — so what arrives in the overview is true at the
 * instant of the response and ages from there. This ticks a local clock once a
 * second and counts the server's remainder down against it.
 *
 * When the countdown hits zero it emits `elapsed` and shows nothing new until
 * the store answers with a fresh overview. Incrementing the number locally
 * would be the client deciding what a player owns, and a client that decides
 * that is a client whose display and the server's truth diverge quietly.
 *
 * ## Two variants, one implementation
 *
 * `panel` is the tall block this was written as. `bar` is the strip in the
 * garage's title bar: the same reading and the same countdown, laid out to fit a
 * row of chrome. **A variant rather than a second component**, for the reason
 * `TitleBadge` has three of them — the clock, the `elapsed` guard and the
 * fill-to-capacity arithmetic are the whole component, and a compact copy of
 * them would be a second thing to keep in step with `fuelCountdown`.
 *
 * The `bar` is a **readout, not a control**: no refill button, because a spend
 * has to be confirmed and the Shop tab's Instant fuel card already words that
 * purchase. A title bar is where you learn you are low, not where you buy.
 */
const props = withDefaults(
  defineProps<{
    wallet: Wallet;
    /** When the wallet snapshot arrived. The countdown measures from here. */
    loadedAtMs: number;
    busy?: boolean;
    /** `panel` is the standalone block; `bar` is the title-bar strip. */
    variant?: 'panel' | 'bar';
  }>(),
  { variant: 'panel' },
);

const emit = defineEmits<{ elapsed: []; refill: [] }>();

const nowMs = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timer = setInterval(() => {
    nowMs.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

const countdown = computed(() =>
  fuelCountdown(props.wallet, nowMs.value - props.loadedAtMs),
);

/**
 * Ask once per expiry, not once per tick. Without the guard, every second past
 * zero would fire another request while the answer was still in flight.
 */
let asked = false;
watch(
  () => props.loadedAtMs,
  () => {
    asked = false;
  },
);
watch(
  () => countdown.value.elapsed,
  (elapsed) => {
    if (elapsed && !asked) {
      asked = true;
      emit('elapsed');
    }
  },
);

const canDeploy = computed(() => countdown.value.fuel >= props.wallet.sortieFuelCost);
</script>

<template>
  <section
    v-if="props.variant === 'bar'"
    class="strip"
    data-test="fuel-gauge"
    :class="{ empty: !canDeploy }"
    :title="
      countdown.full
        ? `Fuel ${countdown.fuel}/${countdown.capacity} — tank full. ${wallet.sortieFuelCost} per sortie.`
        : `Fuel ${countdown.fuel}/${countdown.capacity} — next unit in ${countdown.label}. ` +
          `${wallet.sortieFuelCost} per sortie.`
    "
  >
    <span
      class="strip-count"
      data-test="fuel-count"
      role="meter"
      :data-value="countdown.fuel"
      :aria-valuenow="countdown.fuel"
      :aria-valuemax="countdown.capacity"
      :aria-label="`Fuel ${countdown.fuel} of ${countdown.capacity}`"
    >{{ countdown.fuel }}</span>

    <FuelIcon />

  </section>

  <section v-else class="fuel" data-test="fuel-gauge" :class="{ empty: !canDeploy }">
    <header>
      <span class="label">Fuel</span>
      <span class="count" data-test="fuel-count">
        {{ countdown.fuel }}<em>/{{ countdown.capacity }}</em>
      </span>
    </header>

    <div class="track" role="meter" :aria-valuenow="countdown.fuel" :aria-valuemax="countdown.capacity">
      <i :style="{ width: `${Math.round(countdown.fill * 100)}%` }"></i>
    </div>

    <p class="status">
      <template v-if="countdown.full">
        <span class="full">Tank full</span>
      </template>
      <template v-else>
        <span class="timer" data-test="fuel-countdown">+1 in {{ countdown.label }}</span>
      </template>
      <span class="cost">{{ wallet.sortieFuelCost }} per sortie</span>
    </p>

    <button
      v-if="!countdown.full"
      type="button"
      class="refill"
      data-test="fuel-refill"
      :disabled="busy || wallet.diamonds < wallet.fuelRefillDiamondCost"
      @click="emit('refill')"
    >
      Refill now · {{ wallet.fuelRefillDiamondCost }} <CurrencyIcon kind="diamonds" />
    </button>

    <p v-if="!canDeploy" class="warn" data-test="fuel-empty">
      Out of fuel. Wait for the next unit or refill with diamonds.
    </p>
  </section>
</template>

<style scoped>
/*
 * ─── The title-bar strip ────────────────────────────────────────────────────
 *
 * Everything is in `em`, so the whole thing scales with whatever font size the
 * bar gives it and needs no second tuning pass when the chrome changes height.
 * It is `flex: none`, because `.account` is the cluster that must never give way
 * — the tab strip scrolls instead, and anything here that stretched would be
 * taking the space that rule protects. The countdown keeps its own `min-width`,
 * which is now the only fixed dimension in the strip and the one that matters:
 * without it a clock crossing 10:00 to 9:59 shuffles every control to its right
 * once a second.
 */
.strip {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  /*
     One figure sets the whole strip, everything below it being in `em`, so it
     scales with the title bar rather than being tuned separately — which is the
     failure that leaves a strip a few pixels out of step after every type
     change. `--bar-scale` is `GarageScreen`'s, and the fallback is what keeps
     this component correct anywhere that does not set it.
  */
  /* The balances' own size, not the 0.7rem this had as a three-figure strip.
     With the label and the countdown gone it is a mark and a number, which is
     exactly what the two money chips beside it are — so it is set as their peer,
     which is 0.8rem now that the whole row reads at one size (see `.credits`,
     `.who` and `.tabs button` in `GarageScreen.vue`). At 0.7rem the jerry can
     rendered 11px against the coin's 16 and read as a box with a slash through
     it; the handle and the cap were sub-pixel. */
  font-size: calc(0.8rem * var(--bar-scale, 1));
  line-height: 1;
}

/*
 * The can stays at its own tested size — `FuelIcon`'s `1em` box no longer
 * means what it did once `.strip` dropped to 0.8rem: at 0.7rem this same icon
 * measured 11px and "read as a box with a slash through it", which is the
 * exact failure a naive em-follows-the-text would reintroduce at 0.8rem
 * (12.8px, barely better). Pinned back to what 1rem gave it, in `rem` rather
 * than `em` so it no longer tracks the surrounding font-size at all.
 */
.strip :deep(.wrap) {
  width: calc(1rem * var(--bar-scale, 1));
  height: calc(1rem * var(--bar-scale, 1));
}

/* Empty is the one state worth colouring, and it is coloured the way the panel
   colours it rather than in the HUD accent: orange here would compete with the
   credits balance two elements along. The count is the only thing left to
   colour, which is also the element that was already carrying it — and the mark
   beside it deliberately does not follow, or a low tank would read as two things
   being wrong instead of one figure being low. */
.strip.empty .strip-count {
  color: #d9705f;
}

.strip-count {
  font-family: ui-monospace, 'SF Mono', monospace;
  color: #fff;
  /* Two digits' worth, so a tank crossing 10 → 9 does not shuffle the controls
     to its right. It is the same argument the countdown's `min-width` made, and
     the only one of that element's properties still needed. */
  min-width: 1.4em;
  text-align: right;
}

/* ─── The standalone panel ──────────────────────────────────────────────────── */
.fuel {
  padding: 0.85rem 1rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-800);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.fuel.empty {
  border-color: #8a4a3d;
}

header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.label {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.count {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 1.05rem;
  color: #fff;
}

.count em {
  font-style: normal;
  font-size: 0.72rem;
  color: var(--steel-400);
}

.track {
  height: 8px;
  border-radius: 2px;
  background: var(--steel-700);
  overflow: hidden;
}

.track i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #4c8fbd, #7fc4e8);
  transition: width 0.4s ease;
}

.fuel.empty .track i {
  background: #8a4a3d;
}

.status {
  margin: 0;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: var(--steel-400);
}

.timer {
  font-family: ui-monospace, 'SF Mono', monospace;
  color: var(--steel-200);
}

.full {
  color: #7fc48c;
}

.refill {
  padding: 0.45rem 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-700);
  color: var(--steel-200);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
}

.refill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.warn {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: #d9705f;
}
</style>
